import {
  Chip,
  ChipDelete,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  Clipboard as ClipboardIcon,
  Info,
  MagnifyingGlass,
  Notepad as NotepadIcon,
} from '@phosphor-icons/react';
import { skipToken } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { Command } from 'cmdk';
import { ComponentPropsWithoutRef, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';

import { KeywordAwareInput } from './KeywordAwareInput';

import { CommandDialog, CommandDialogContent } from '../command';

import {
  AutoComplete,
  isOptionShape,
  optionShapeSchema,
} from '~/components/ui/autocomplete';
import { Button } from '~/components/ui/button';
import { Card, CardOverflow } from '~/components/ui/card';
import { Checkbox, CheckboxField } from '~/components/ui/checkbox';
import { DateRangePickerField } from '~/components/ui/date-picker';
import { DialogTrigger } from '~/components/ui/dialog';
import { FieldGroup, Fieldset, Legend } from '~/components/ui/fieldset';
import { Field, FormController, useFormController } from '~/components/ui/form';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '~/components/ui/hover-card';
import { TextLink } from '~/components/ui/link';
import getStatusOptions from '~/constants/statusOptions';
import { useLanguage } from '~/contexts/lang/languageContext';
import { useTenantSlug } from '~/features/tenant/helpers';
import QueryResolver from '~/services/api/query-resolver';
import { ReportService } from '~/services/api/reports/endpoints';
import {
  ReportSearchResult,
  SearchResultFieldOrigin,
} from '~/services/api/reports/types';
import { TenantService } from '~/services/api/tenant/endpoints';
import { UtilityService } from '~/services/api/utilities/endpoints';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';
import { useForm } from '~/utils/forms';
import { toPayloadDateFormat } from '~/utils/helpers';

const getReportStatusLabel = (status: number) => {
  return getStatusOptions().find((item) => item.id === status)?.name ?? '---';
};

const getPriorityId = (priority: string) => {
  const priorityList = [
    { id: 0, name: 'Low' },
    { id: 1, name: 'Medium' },
    { id: 2, name: 'High' },
    { id: 3, name: 'Critical' },
  ];
  return priorityList.find(
    (item) => item.name.toLowerCase() === priority.toLowerCase(),
  )?.id;
};

const schema = z.object({
  searchQuery: z.string(),
  date: z
    .object({
      from: z.coerce.date(),
      to: z.coerce.date(),
    })
    .optional(),
  reportType: z.array(optionShapeSchema),
  category: z.array(optionShapeSchema),
  severity: z.record(z.string(), z.boolean()),
});

export default function AdvancedSearch() {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;

  const form = useForm({
    schema,
    defaultValues: {
      reportType: [],
      category: [],
      severity: {},
      searchQuery: '',
    },
  });

  const searchQuery = form.watch('searchQuery');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  const selectedReportTypes = form
    .watch('reportType')
    .map((item) => Number(item.id));

  const selectedCategories = form
    .watch('category')
    .map((item) => Number(item.id));

  const selectedSeverity = Object.entries(form.watch('severity'))
    .filter((entry) => !!entry[1])
    .map((entry) => getPriorityId(entry[0]));

  const dateRange = form.watch('date');
  const isRange = dateRange && dateRange.from && dateRange.to;

  const query = ReportService.endpoints.reportSearch.useQuery(
    {
      params: {
        form_id: selectedChannel ?? 0,
      },
      payload: {
        search_text: debouncedSearchQuery,
        final_due_date: isRange
          ? [
              toPayloadDateFormat(dateRange.from),
              toPayloadDateFormat(dateRange.to),
            ]
          : undefined,
        report_type: selectedReportTypes.length
          ? selectedReportTypes
          : undefined,
        comments__category: selectedCategories.length
          ? selectedCategories
          : undefined,
        priority: selectedSeverity.length
          ? selectedSeverity.filter(
              (value): value is number =>
                z.coerce.number().safeParse(value).success,
            )
          : undefined,
      },
    },
    {
      skip: debouncedSearchQuery.length === 0 || !selectedChannel,
    },
  );
  const hasEmptyResults = query.isSuccess && query.data.length === 0;
  const hasSearchQuery = searchQuery.length !== 0;

  return (
    <CommandDialog
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          form.reset();
        }

        /**
         * This alters the hubspot `z-index` value to 0 to prevent it from
         * being interactive while the dialog is open
         */
        const hubspot = document.getElementById(
          'hubspot-messages-iframe-container',
        );
        if (hubspot) {
          hubspot.style.zIndex = nextOpen ? '0' : '';
        }
      }}
    >
      <DialogTrigger asChild>
        <IconButton
          size='md'
          variant='outlined'
          sx={{ bgcolor: (theme) => theme.palette.background.surface }}
        >
          <MagnifyingGlass />
        </IconButton>
      </DialogTrigger>
      <CommandDialogContent size='3xl'>
        <Command shouldFilter={false}>
          <Card>
            <CardOverflow sx={{ px: 0 }}>
              <div className='divide-y'>
                <div className='p-2'>
                  <FormController
                    control={form.control}
                    field='searchQuery'
                    aria-label='Search Term'
                  >
                    <KeywordAwareInput />
                  </FormController>
                </div>
                <div className={clsx(['flex flex-col', 'h-[80vh]'])}>
                  <section className='flex-1 flex divide-x overflow-hidden'>
                    <section className='flex-1 min-w-0 flex flex-col overflow-hidden'>
                      {searchQuery.length !== 0 && (
                        <div className='px-4 pt-4'>
                          <Typography level='title-sm'>
                            {intl.formatMessage({
                              defaultMessage: 'Reports and Investigation Notes',
                            })}
                          </Typography>
                        </div>
                      )}
                      <Command.List
                        className={clsx([
                          'flex-1 flex flex-col overflow-y-auto',
                          'has-[[cmdk-empty]]:items-center has-[[cmdk-empty]]:justify-center',
                        ])}
                      >
                        <Command.Empty>
                          {/* No search input */}
                          {!hasSearchQuery && (
                            <div className='flex justify-center items-center h-full p-4 text-center'>
                              <Typography
                                component='span'
                                level='body-sm'
                                className='space-y-4'
                              >
                                <p>
                                  <FormattedMessage
                                    defaultMessage='Use quotation marks for exact matches, and Boolean operators ( <bold>AND</bold>, <bold>OR</bold>, <bold>NOT</bold>) to refine your search.'
                                    values={{
                                      bold: (chunks) => (
                                        <Typography
                                          component='span'
                                          fontWeight={600}
                                        >
                                          {chunks}
                                        </Typography>
                                      ),
                                    }}
                                  />
                                </p>
                                <p>
                                  {intl.formatMessage({
                                    defaultMessage:
                                      'Apply filters to narrow results by date range, report type, and other criteria.',
                                  })}
                                </p>
                                <p>
                                  {intl.formatMessage({
                                    defaultMessage:
                                      'These advanced options help you find precise and relevant information quickly.',
                                  })}
                                </p>
                              </Typography>
                            </div>
                          )}

                          {/* Loading */}
                          {query.isLoading && hasSearchQuery && (
                            <div className='relative'>
                              <CircularProgress size='md' />
                            </div>
                          )}

                          {/* API Error  */}
                          {query.isError && hasSearchQuery && (
                            <div className='flex items-center justify-center'>
                              <div>
                                <div>
                                  <Typography level='title-lg'>
                                    {intl.formatMessage({
                                      defaultMessage: 'Error',
                                    })}
                                  </Typography>
                                  <Typography>
                                    {intl.formatMessage({
                                      defaultMessage:
                                        'Failed to load results. Try again.',
                                    })}
                                  </Typography>
                                </div>
                                <div className='mt-2'>
                                  <Button
                                    color='neutral'
                                    variant='soft'
                                    size='md'
                                    onClick={query.refetch}
                                  >
                                    {intl.formatMessage({
                                      defaultMessage: 'Try Again',
                                    })}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Empty Results */}
                          {hasEmptyResults && hasSearchQuery && (
                            <div className='flex flex-col items-center space-y-2'>
                              <MagnifyingGlass size={40} />
                              <Typography>
                                {intl.formatMessage({
                                  defaultMessage: 'No results found',
                                })}
                              </Typography>
                            </div>
                          )}
                        </Command.Empty>

                        <QueryResolver
                          query={query}
                          loadingElement={null}
                          errorElement={<></>}
                        >
                          {(results) =>
                            results.map((result) => (
                              <Command.Item
                                value={result.formatted_report_number}
                                key={result.reference_id}
                                className={clsx([
                                  'data-[selected=true]:bg-brand-primary-50 p-3 cursor-pointer group relative',
                                ])}
                              >
                                <SearchResult result={result} />
                              </Command.Item>
                            ))
                          }
                        </QueryResolver>
                      </Command.List>
                    </section>
                    <section className='w-80 shrink-0 p-4 overflow-y-auto'>
                      <form>
                        <FieldGroup>
                          <Fieldset>
                            <Legend>
                              {intl.formatMessage({
                                defaultMessage: 'Filter By Date',
                              })}
                            </Legend>
                            <FieldGroup>
                              <DateRangePickerField
                                control={form.control}
                                field='date'
                                description={
                                  <div className='flex justify-end items-center'>
                                    <Button
                                      size='sm'
                                      variant='plain'
                                      color='neutral'
                                      onClick={() => form.resetField('date')}
                                      disabled={!isRange}
                                      sx={{
                                        p: 0,
                                        minHeight: 'auto',
                                        ':hover': {
                                          backgroundColor: 'transparent',
                                        },
                                      }}
                                    >
                                      Clear
                                    </Button>
                                  </div>
                                }
                              />
                            </FieldGroup>
                          </Fieldset>
                          <Fieldset>
                            <Legend>
                              {intl.formatMessage({
                                defaultMessage: 'Filter by report type',
                              })}
                            </Legend>
                            <FieldGroup>
                              <Field control={form.control} field='reportType'>
                                <ReportTypeFilter />
                              </Field>
                            </FieldGroup>
                          </Fieldset>
                          <Fieldset>
                            <Legend>
                              {intl.formatMessage({
                                defaultMessage: 'Filter by category',
                              })}
                            </Legend>
                            <FieldGroup>
                              <Field control={form.control} field='category'>
                                <CategoryFilter />
                              </Field>
                            </FieldGroup>
                          </Fieldset>
                          <Fieldset>
                            <Legend>
                              {intl.formatMessage({
                                defaultMessage: 'Filter by severity',
                              })}
                            </Legend>
                            <FieldGroup>
                              <div className='grid grid-cols-2 gap-4'>
                                {['Critical', 'Low', 'Medium', 'High'].map(
                                  (name) => (
                                    <CheckboxField
                                      key={name}
                                      control={form.control}
                                      field={`severity.${name}` as const}
                                      label={name}
                                    />
                                  ),
                                )}
                              </div>
                            </FieldGroup>
                          </Fieldset>
                        </FieldGroup>
                      </form>
                    </section>
                  </section>
                </div>
                <div className='py-2'>
                  <Tooltip
                    placement='top'
                    variant='outlined'
                    arrow
                    title={
                      <div className='max-w-sm p-2'>
                        <Typography component='span' level='body-sm'>
                          <p>
                            <FormattedMessage
                              defaultMessage='Use quotation marks for exact matches, and Boolean operators ( <bold>AND</bold>, <bold>OR</bold>, <bold>NOT</bold>) to refine your search.'
                              values={{
                                bold: (chunks) => (
                                  <Typography component='span' fontWeight={600}>
                                    {chunks}
                                  </Typography>
                                ),
                              }}
                            />
                          </p>
                          <p>
                            {intl.formatMessage({
                              defaultMessage:
                                'Apply filters to narrow results by date range, report type, and other criteria.',
                            })}
                          </p>
                        </Typography>
                      </div>
                    }
                  >
                    <Button
                      startDecorator={<Info weight='fill' />}
                      variant='plain'
                      color='neutral'
                    >
                      {intl.formatMessage({ defaultMessage: 'Search Guide' })}
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </CardOverflow>
          </Card>
        </Command>
      </CommandDialogContent>
    </CommandDialog>
  );
}

const encryptedContentOrigin: Array<SearchResultFieldOrigin> = [
  'additional_informations',
  'description_text',
];
function SearchResult({ result }: { result: ReportSearchResult }) {
  const intl = useIntl();
  const [contentOrigin, contents] = Object.entries(result.highlighted_text)[0];
  const resultContent = contents.find((item) => !!item) ?? '---';

  const needsDecryption = encryptedContentOrigin.some(
    (item) => item === contentOrigin,
  );

  const isFieldReport = result.field.every((item) => item === 'Report');
  const isFieldInvestigatorNotes = result.field.every(
    (item) => item === 'Investigator Notes',
  );
  const isResultFromAllFields =
    result.field.length > 1 &&
    result.field.every(
      (item) => item === 'Report' || item === 'Investigator Notes',
    );

  return (
    <div
      className={clsx([
        'flex items-start',
        isResultFromAllFields ? 'gap-2' : 'gap-1',
      ])}
    >
      <div className='flex relative z-0'>
        <span
          className={clsx([
            'flex items-center justify-center rounded size-6 shrink-0 z-10',
            isFieldInvestigatorNotes && 'bg-yellow-400',
            (isFieldReport || isResultFromAllFields) && 'bg-brand-primary-500',
          ])}
        >
          {isFieldInvestigatorNotes && (
            <NotepadIcon weight='fill' className='size-4 text-white' />
          )}

          {(isFieldReport || isResultFromAllFields) && (
            <ClipboardIcon weight='fill' className='size-4 text-white' />
          )}
        </span>
        {isResultFromAllFields && (
          <span className='rounded size-6 bg-yellow-400 shrink-0 absolute inset-x-0 translate-y-1 translate-x-1'>
            <NotepadIcon weight='fill' className='size-4 text-white' />
          </span>
        )}
      </div>
      <div className='group-data-[selected=true]:underline'>
        <HoverCard>
          <HoverCardTrigger>
            <Typography level='title-sm'>
              {result.formatted_report_number}
              <span aria-hidden='true' className='absolute inset-0' />
            </Typography>
          </HoverCardTrigger>
          <HoverCardContent side='right' align='start' bleed asChild>
            <Card size='md' className='w-96'>
              <div className='flex items-center justify-between'>
                <div
                  className={clsx([
                    'flex items-center',
                    isResultFromAllFields ? 'gap-2' : 'gap-1',
                  ])}
                >
                  <div className='flex relative z-0'>
                    <span
                      className={clsx([
                        'flex items-center justify-center rounded size-6 shrink-0 z-10',
                        isFieldInvestigatorNotes && 'bg-yellow-400',
                        (isFieldReport || isResultFromAllFields) &&
                          'bg-brand-primary-500',
                      ])}
                    >
                      {isFieldInvestigatorNotes && (
                        <NotepadIcon
                          weight='fill'
                          className='size-4 text-white'
                        />
                      )}

                      {(isFieldReport || isResultFromAllFields) && (
                        <ClipboardIcon
                          weight='fill'
                          className='size-4 text-white'
                        />
                      )}
                    </span>
                    {isResultFromAllFields && (
                      <span className='rounded size-6 bg-yellow-400 shrink-0 absolute inset-x-0 translate-y-1 translate-x-1'>
                        <NotepadIcon
                          weight='fill'
                          className='size-4 text-white'
                        />
                      </span>
                    )}
                  </div>
                  <Typography level='title-sm'>
                    {result.formatted_report_number}
                  </Typography>
                </div>
                <TextLink
                  href={`/customer/reports/${result.reference_id}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  color='secondary'
                  level='title-sm'
                  fontWeight={600}
                >
                  {intl.formatMessage({
                    defaultMessage: 'View',
                  })}
                </TextLink>
              </div>
              <div>
                <dl className='grid grid-cols-3 gap-2'>
                  <div>
                    <dd className='flex'>
                      <Tooltip title={result.user_full_name}>
                        <Typography level='title-sm' className='truncate'>
                          {result.user_full_name ?? '---'}
                        </Typography>
                      </Tooltip>
                    </dd>
                    <dt>
                      <Typography level='body-sm'>
                        {intl.formatMessage({
                          defaultMessage: 'Reporter',
                        })}
                      </Typography>
                    </dt>
                  </div>
                  <div>
                    <dd className='flex'>
                      <Tooltip title={result.report_type_name}>
                        <Typography level='title-sm' className='truncate'>
                          {result.report_type_name}
                        </Typography>
                      </Tooltip>
                    </dd>
                    <dt>
                      <Typography level='body-sm'>
                        {intl.formatMessage({
                          defaultMessage: 'Report Type',
                        })}
                      </Typography>
                    </dt>
                  </div>
                  <div>
                    <dd className='flex'>
                      <Tooltip title={getReportStatusLabel(result.status)}>
                        <Typography level='title-sm' className='truncate'>
                          {getReportStatusLabel(result.status)}
                        </Typography>
                      </Tooltip>
                    </dd>
                    <dt>
                      <Typography level='body-sm'>
                        {intl.formatMessage({
                          defaultMessage: 'Status',
                        })}
                      </Typography>
                    </dt>
                  </div>
                  <div className='col-span-full'>
                    <dt className='sr-only'>
                      {intl.formatMessage({
                        defaultMessage: 'Details Preview',
                      })}
                    </dt>
                    <dd className='line-clamp-3'>
                      <Typography level='body-sm' component='span'>
                        Details:
                        {needsDecryption ? (
                          <DecryptedContent
                            content={resultContent}
                            className='prose prose-sm'
                          />
                        ) : (
                          <p
                            className='prose prose-sm'
                            dangerouslySetInnerHTML={{
                              __html: resultContent,
                            }}
                          />
                        )}
                      </Typography>
                    </dd>
                  </div>
                </dl>
              </div>
            </Card>
          </HoverCardContent>
        </HoverCard>
        <Typography level='body-sm'>{result.field.join(', ')}</Typography>
      </div>
    </div>
  );
}

function DecryptedContent({
  content,
  ...props
}: { content: string } & Omit<ComponentPropsWithoutRef<'div'>, 'content'>) {
  const query = UtilityService.endpoints.decryptText.useQuery(
    content ? { content } : skipToken,
    { skip: !content },
  );
  if (!content) return null;

  return (
    <QueryResolver query={query}>
      {(decryptedValue) => (
        <div {...props} dangerouslySetInnerHTML={{ __html: decryptedValue }} />
      )}
    </QueryResolver>
  );
}

function CategoryFilter() {
  const intl = useIntl();

  const controller = useFormController();
  const field = controller?.field;
  const query = TenantService.endpoints.tenantNoteCategories.useQuery();
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  if (!field) {
    return null;
  }

  const value = schema
    .pick({ category: true })
    .catch({ category: [] })
    .parse({ category: field.value }).category;

  return (
    <div>
      <Skeleton loading={query.isLoading}>
        <QueryResolver query={query} loadingElement={null}>
          {(reportTypes) => (
            <AutoComplete
              name={field.name}
              inputValue={inputValue}
              placeholder={intl.formatMessage({
                defaultMessage: 'Select report type',
              })}
              getOptionKey={(option) => {
                if (typeof option !== 'string') {
                  return option.id;
                }
                return option;
              }}
              options={reportTypes
                .filter(
                  (item) => !value.some((v) => v.id === item.id.toString()),
                )
                .map((reportType) => ({
                  label: reportType.name,
                  id: reportType.id.toString(),
                }))}
              onChange={(_event, newValue) => {
                if (newValue && isOptionShape(newValue)) {
                  field.value = [
                    ...field.value,
                    { id: newValue.id, label: newValue.label },
                  ];
                  field.onChange(field.value);
                  setInputValue('');
                }
              }}
              onBlur={field.onBlur}
              // eslint-disable-next-line @typescript-eslint/no-shadow
              onInputChange={(_event, value, reason) => {
                if (reason !== 'reset') {
                  setInputValue(value);
                }
              }}
              slotProps={{
                root: { ref: containerRef },
                listbox: {
                  container: () => containerRef.current,
                  popperOptions: { strategy: 'absolute' },
                },
              }}
              filterSelectedOptions
              disableClearable
            />
          )}
        </QueryResolver>
      </Skeleton>
      <div className='max-h-40 h-full mt-4 overflow-y-auto'>
        <List
          orientation='horizontal'
          size='sm'
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          {value.map((report) => (
            <ListItem
              key={report.id}
              sx={{
                '--ListItem-paddingLeft': 0,
                '--ListItem-paddingRight': 0,
              }}
            >
              <Chip
                endDecorator={
                  <ChipDelete
                    onDelete={() => {
                      field.value = value.filter((v) => v.id !== report.id);
                      field.onChange(field.value);
                    }}
                  />
                }
              >
                <Checkbox
                  variant='outlined'
                  disableIcon
                  overlay
                  label={report.label}
                  controlled={false}
                  size='sm'
                />
              </Chip>
            </ListItem>
          ))}
        </List>
      </div>
    </div>
  );
}

function ReportTypeFilter() {
  const intl = useIntl();

  const controller = useFormController();
  const field = controller?.field;
  const slug = useTenantSlug();
  const channelId = useIntakeChannel().intakeChannelData.selectedChannel;
  const language = useLanguage().selectedLanguage;
  const query = TenantService.endpoints.intakeChannelProgramDetails.useQuery(
    {
      slug: slug,
      intakeChannelId: channelId ?? 0,
      params: {
        language_id: language.id,
      },
    },
    {
      skip: !slug || !channelId,
    },
  );
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  if (!field) {
    return null;
  }

  const value = schema
    .pick({ reportType: true })
    .catch({ reportType: [] })
    .parse({ reportType: field.value }).reportType;

  return (
    <div>
      <Skeleton loading={query.isLoading}>
        <QueryResolver query={query} loadingElement={null}>
          {(data) => (
            <AutoComplete
              name={field.name}
              inputValue={inputValue}
              placeholder={intl.formatMessage({
                defaultMessage: 'Select report type',
              })}
              getOptionKey={(option) => {
                if (typeof option !== 'string') {
                  return option.id;
                }
                return option;
              }}
              options={data.report_types
                .filter(
                  (item) => !value.some((v) => v.id === item.id.toString()),
                )
                .map((reportType) => ({
                  label: reportType.name,
                  id: reportType.id.toString(),
                }))}
              onChange={(_event, newValue) => {
                if (newValue && isOptionShape(newValue)) {
                  field.value = [
                    ...field.value,
                    { id: newValue.id, label: newValue.label },
                  ];
                  field.onChange(field.value);
                  setInputValue('');
                }
              }}
              onBlur={field.onBlur}
              // eslint-disable-next-line @typescript-eslint/no-shadow
              onInputChange={(_event, value, reason) => {
                if (reason !== 'reset') {
                  setInputValue(value);
                }
              }}
              slotProps={{
                root: { ref: containerRef },
                listbox: {
                  container: () => containerRef.current,
                  popperOptions: { strategy: 'absolute' },
                },
              }}
              filterSelectedOptions
              disableClearable
            />
          )}
        </QueryResolver>
      </Skeleton>
      <div className='max-h-40 h-full mt-4 overflow-y-auto'>
        <List
          orientation='horizontal'
          size='sm'
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          {value.map((report) => (
            <ListItem
              key={report.id}
              sx={{
                '--ListItem-paddingLeft': 0,
                '--ListItem-paddingRight': 0,
              }}
            >
              <Chip
                endDecorator={
                  <ChipDelete
                    onDelete={() => {
                      field.value = value.filter((v) => v.id !== report.id);
                      field.onChange(field.value);
                    }}
                  />
                }
              >
                <Checkbox
                  variant='outlined'
                  disableIcon
                  overlay
                  label={report.label}
                  controlled={false}
                  size='sm'
                />
              </Chip>
            </ListItem>
          ))}
        </List>
      </div>
    </div>
  );
}
